import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import TrackSearch from './TrackSearch';
import { toast } from 'sonner';
import {
  ChevronDown, Search, ListMusic, Link2, Sparkles, X, GripVertical,
  Music, Plus, Check, RefreshCw, Loader2, Trash2, Download,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Playlist Creator / Generator dialog (Feb 2026 rewrite).
//
// Tab 1 (Creator): manual curation — search individual songs, browse
//   community playlists on YT Music, or paste a YT playlist URL.
// Tab 2 (Generator): auto-curation — pick 3-5 seed songs, backend
//   returns 50 related tracks; per-row swap + drag reorder + delete.
//
// Both tabs share the working-list + save handler passed in via props.
// ---------------------------------------------------------------------------

const fmtDuration = (ms) => {
  if (!ms) return '';
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Sortable row used by both tabs. Optional `onSwap` renders a shuffle button
// (Generator tab). Optional `swapping` shows a spinner during the swap call.
const SortableTrackRow = ({ track, index, onRemove, onSwap, swapping }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: track.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/8 group"
      data-testid={`playlist-track-row-${index}`}
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1" aria-label="Drag to reorder">
        <GripVertical className="w-4 h-4 text-gray-500" />
      </button>
      <span className="text-gray-500 text-xs font-mono w-6 text-right">{index + 1}</span>
      {track.album_image ? (
        <img src={track.album_image} alt="" className="w-9 h-9 rounded object-cover" />
      ) : (
        <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center"><Music className="w-4 h-4 text-white/40" /></div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{track.name}</p>
        <p className="text-gray-400 text-xs truncate">{Array.isArray(track.artists) ? track.artists.join(', ') : track.artists}</p>
      </div>
      {track.duration_ms > 0 && <span className="text-gray-500 text-xs">{fmtDuration(track.duration_ms)}</span>}
      {onSwap && (
        <Button
          variant="ghost" size="icon"
          onClick={() => onSwap(track.id)}
          disabled={swapping}
          className="h-7 w-7 hover:bg-[#FF007F]/20 opacity-0 group-hover:opacity-100"
          title="Swap for a different track"
          data-testid={`swap-track-${index}`}
        >
          {swapping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 text-[#FF007F]" />}
        </Button>
      )}
      <Button
        variant="ghost" size="icon"
        onClick={() => onRemove(track.id)}
        className="h-7 w-7 hover:bg-red-500/20 opacity-0 group-hover:opacity-100"
        title="Remove"
        data-testid={`remove-track-${index}`}
      >
        <X className="w-3.5 h-3.5 text-red-400" />
      </Button>
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, subtitle, open }) => (
  <div className="flex items-center justify-between w-full p-3 rounded-lg bg-white/5 hover:bg-white/8 transition-colors">
    <div className="flex items-center gap-3 min-w-0">
      <Icon className="w-4 h-4 text-[#FF007F] flex-shrink-0" />
      <div className="text-left min-w-0">
        <p className="text-white text-sm font-semibold truncate">{title}</p>
        {subtitle && <p className="text-gray-500 text-xs truncate">{subtitle}</p>}
      </div>
    </div>
    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
  </div>
);

// Community-playlist browser (search + open-to-tracks + bulk add).
const CommunityPlaylistBrowser = ({ existingIds, onAddTracks }) => {
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedTracks, setExpandedTracks] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);

  const search = useCallback(async () => {
    const term = q.trim();
    if (!term) return;
    setSearching(true);
    setPlaylists([]);
    setExpandedId(null);
    try {
      const r = await axios.get(`${API}/music/community-playlists/search`, { params: { q: term, limit: 12 } });
      setPlaylists(r.data?.playlists || []);
    } catch (e) {
      toast.error('Community playlist search failed');
    } finally {
      setSearching(false);
    }
  }, [q]);

  const openPlaylist = async (pl) => {
    if (expandedId === pl.playlist_id) {
      setExpandedId(null);
      setExpandedTracks([]);
      return;
    }
    setExpandedId(pl.playlist_id);
    setExpandedTracks([]);
    setLoadingTracks(true);
    try {
      const r = await axios.get(`${API}/music/community-playlists/${encodeURIComponent(pl.playlist_id)}/tracks`, { params: { limit: 200 } });
      setExpandedTracks(r.data?.tracks || []);
    } catch (e) {
      toast.error("Couldn't load that playlist");
    } finally {
      setLoadingTracks(false);
    }
  };

  const addAll = () => {
    const fresh = expandedTracks.filter((t) => !existingIds.includes(t.id));
    if (fresh.length === 0) {
      toast.info('All tracks already added');
      return;
    }
    onAddTracks(fresh);
    toast.success(`Added ${fresh.length} track${fresh.length === 1 ? '' : 's'}`);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="Try 'lofi hip hop', 'bollywood party', 'workout'..."
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            data-testid="community-playlist-search-input"
          />
        </div>
        <Button
          onClick={search} disabled={!q.trim() || searching}
          className="bg-[#FF007F] hover:bg-[#CC0066] text-black"
          data-testid="community-playlist-search-button"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
        </Button>
      </div>

      {playlists.length > 0 && (
        <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="community-playlist-results">
          {playlists.map((pl) => {
            const isOpen = expandedId === pl.playlist_id;
            return (
              <div key={pl.playlist_id} className="rounded-lg bg-white/5 border border-white/5 overflow-hidden">
                <button
                  onClick={() => openPlaylist(pl)}
                  className="w-full flex items-center gap-3 p-2.5 hover:bg-white/8 text-left"
                  data-testid={`community-playlist-${pl.playlist_id}`}
                >
                  {pl.thumbnail_url ? (
                    <img src={pl.thumbnail_url} alt="" className="w-11 h-11 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded bg-white/10 flex items-center justify-center flex-shrink-0"><ListMusic className="w-5 h-5 text-white/40" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{pl.name}</p>
                    <p className="text-gray-400 text-xs truncate">{pl.author}{pl.track_count ? ` · ${pl.track_count} tracks` : ''}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div className="border-t border-white/5 p-2 bg-black/20">
                    {loadingTracks ? (
                      <div className="flex items-center justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-[#FF007F]" /></div>
                    ) : expandedTracks.length === 0 ? (
                      <p className="text-gray-500 text-xs text-center py-3">No playable tracks</p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-2 px-1">
                          <span className="text-xs text-gray-400">{expandedTracks.length} tracks</span>
                          <Button
                            size="sm" onClick={addAll}
                            className="h-7 px-3 bg-[#FF007F] hover:bg-[#CC0066] text-black text-xs"
                            data-testid={`add-all-tracks-${pl.playlist_id}`}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add all
                          </Button>
                        </div>
                        <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                          {expandedTracks.map((t) => {
                            const added = existingIds.includes(t.id);
                            return (
                              <div key={t.id} className="flex items-center gap-2 p-1.5 rounded bg-white/[0.03]">
                                {t.album_image && <img src={t.album_image} alt="" className="w-8 h-8 rounded object-cover" />}
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-xs truncate">{t.name}</p>
                                  <p className="text-gray-500 text-[10px] truncate">{Array.isArray(t.artists) ? t.artists.join(', ') : t.artists}</p>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => !added && onAddTracks([t])}
                                  disabled={added}
                                  className={`h-6 w-6 p-0 ${added ? 'bg-green-600/20 text-green-400' : 'bg-white/10 hover:bg-white/15'}`}
                                >
                                  {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================ MAIN DIALOG ================================

const PlaylistCreatorDialog = ({ open, onOpenChange, onSaved }) => {
  const [activeTab, setActiveTab] = useState('creator');

  // -- Shared name field (per tab so switching tabs doesn't clobber) --
  const [creatorName, setCreatorName] = useState('');
  const [generatorName, setGeneratorName] = useState('');

  // -- Creator tab state --
  const [creatorTracks, setCreatorTracks] = useState([]);
  const [openSection, setOpenSection] = useState('songs'); // 'songs' | 'community' | 'url'
  const [ytUrl, setYtUrl] = useState('');
  const [ytImporting, setYtImporting] = useState(false);
  const [importSkipped, setImportSkipped] = useState([]);

  // -- Generator tab state --
  const [seedTracks, setSeedTracks] = useState([]);        // 1..5 seed songs
  const [generatedTracks, setGeneratedTracks] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [swappingId, setSwappingId] = useState(null);

  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const reset = () => {
    setActiveTab('creator');
    setCreatorName(''); setGeneratorName('');
    setCreatorTracks([]); setSeedTracks([]); setGeneratedTracks([]);
    setYtUrl(''); setOpenSection('songs');
    setImportSkipped([]);
  };

  const closeAndReset = () => { reset(); onOpenChange(false); };

  // -------- Creator: add / remove / reorder --------
  const addCreatorTracks = (tracks) => {
    setCreatorTracks((prev) => {
      const seen = new Set(prev.map((t) => t.id));
      const merged = [...prev];
      for (const t of tracks) {
        if (t.id && !seen.has(t.id)) { merged.push(t); seen.add(t.id); }
      }
      return merged;
    });
  };
  const addOneCreatorTrack = (t) => addCreatorTracks([t]);
  const removeCreatorTrack = (id) => setCreatorTracks((prev) => prev.filter((t) => t.id !== id));
  const onCreatorDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = creatorTracks.findIndex((t) => t.id === active.id);
    const newIdx = creatorTracks.findIndex((t) => t.id === over.id);
    setCreatorTracks(arrayMove(creatorTracks, oldIdx, newIdx));
  };

  const importYouTubeUrl = async () => {
    const url = ytUrl.trim();
    if (!url) return;
    setYtImporting(true);
    try {
      const r = await axios.get(`${API}/youtube/playlist/import`, { params: { url } });
      const imported = r.data?.tracks || [];
      const skipped = r.data?.skipped || [];
      setImportSkipped(skipped);
      if (!imported.length) {
        toast.info('No music-category videos found in that playlist');
        return;
      }
      addCreatorTracks(imported);
      setYtUrl('');
      if (skipped.length) {
        toast.warning(`Imported ${imported.length} — skipped ${skipped.length} unavailable song${skipped.length === 1 ? '' : 's'} (listed below)`);
      } else {
        toast.success(`Imported ${imported.length} track${imported.length === 1 ? '' : 's'} from YouTube`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Couldn't import that YouTube playlist");
    } finally {
      setYtImporting(false);
    }
  };

  // -------- Generator: seed / generate / swap / reorder --------
  const addSeed = (t) => {
    if (seedTracks.length >= 5) { toast.error('Max 5 seed songs'); return; }
    if (seedTracks.some((s) => s.id === t.id)) return;
    setSeedTracks((prev) => [...prev, t]);
  };
  const removeSeed = (id) => setSeedTracks((prev) => prev.filter((t) => t.id !== id));

  const generate = async () => {
    if (seedTracks.length < 3) { toast.error('Pick at least 3 seed songs'); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/music/generate-from-seeds`, {
        seed_video_ids: seedTracks.map((s) => s.id),
        limit: 50,
        // Backend excludes seeds server-side already; passing them here
        // too is defensive if the client is ever pointed at a different
        // implementation.
        exclude: seedTracks.map((s) => s.id),
      });
      const gen = r.data?.tracks || [];
      if (!gen.length) {
        toast.error('No related tracks found — try different seeds');
        return;
      }
      setGeneratedTracks(gen);
      toast.success(`Generated ${gen.length} tracks`);
    } catch (e) {
      toast.error('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const swapGeneratedTrack = async (trackId) => {
    setSwappingId(trackId);
    try {
      const currentIds = generatedTracks.map((t) => t.id);
      const seedIds = seedTracks.map((s) => s.id);
      const r = await axios.post(`${API}/music/swap-track`, {
        seed_video_ids: seedIds,
        // Include seeds + everything already in the list so we can never
        // surface a duplicate.
        exclude: Array.from(new Set([...currentIds, ...seedIds])),
      });
      const replacement = r.data?.track;
      if (!replacement) {
        toast.info('No more related tracks to swap in');
        return;
      }
      setGeneratedTracks((prev) => {
        const idx = prev.findIndex((t) => t.id === trackId);
        if (idx === -1) return [...prev, replacement];
        const next = [...prev];
        next[idx] = replacement;
        return next;
      });
    } catch (e) {
      toast.error('Swap failed');
    } finally {
      setSwappingId(null);
    }
  };

  const removeGeneratedTrack = (id) => setGeneratedTracks((prev) => prev.filter((t) => t.id !== id));
  const onGeneratedDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = generatedTracks.findIndex((t) => t.id === active.id);
    const newIdx = generatedTracks.findIndex((t) => t.id === over.id);
    setGeneratedTracks(arrayMove(generatedTracks, oldIdx, newIdx));
  };

  // -------- Save --------
  const save = async () => {
    const isGenerator = activeTab === 'generator';
    const name = (isGenerator ? generatorName : creatorName).trim();
    const tracks = isGenerator ? generatedTracks : creatorTracks;
    if (!name) { toast.error('Playlist name is required'); return; }
    if (!tracks.length) { toast.error('Add at least one track'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/playlists/created`, {
        name,
        description: isGenerator ? `Auto-generated from ${seedTracks.length} seed songs` : '',
        tracks,
      });
      toast.success(`Saved "${name}"`);
      onSaved?.(r.data);
      closeAndReset();
    } catch (e) {
      toast.error('Failed to save playlist');
    } finally {
      setSaving(false);
    }
  };

  const creatorIds = creatorTracks.map((t) => t.id);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        // Only reset when Dialog itself asks to close (X button, ESC, backdrop
        // click). closeAndReset() handles the Cancel/save path directly.
        if (!v && open) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-3xl max-h-[92vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/5">
          <DialogTitle className="font-['Outfit'] text-xl">Playlist Studio</DialogTitle>
          <p className="text-gray-400 text-sm">Build a playlist by hand or generate 50 tracks from a few seeds.</p>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <div className="px-6 pt-4">
            <TabsList className="bg-white/5 border border-white/10 h-10 p-1 rounded-full">
              <TabsTrigger
                value="creator"
                className="rounded-full px-4 data-[state=active]:bg-[#FF007F] data-[state=active]:text-black data-[state=active]:shadow-none text-gray-300"
                data-testid="tab-creator"
              >
                <ListMusic className="w-4 h-4 mr-1.5" /> Playlist Creator
              </TabsTrigger>
              <TabsTrigger
                value="generator"
                className="rounded-full px-4 data-[state=active]:bg-[#FF007F] data-[state=active]:text-black data-[state=active]:shadow-none text-gray-300"
                data-testid="tab-generator"
              >
                <Sparkles className="w-4 h-4 mr-1.5" /> Playlist Generator
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ============= Tab 1: Creator ============= */}
          <TabsContent value="creator" className="flex-1 overflow-y-auto px-6 pt-4 pb-4 space-y-4 mt-0" data-testid="tab-content-creator">
            <div>
              <Label className="mb-2 block">Playlist name *</Label>
              <Input
                value={creatorName}
                onChange={(e) => setCreatorName(e.target.value)}
                placeholder="e.g. Friday Night Rooftop"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="creator-name-input"
              />
            </div>

            {/* Section 1: Search Songs */}
            <Collapsible open={openSection === 'songs'} onOpenChange={(v) => setOpenSection(v ? 'songs' : null)}>
              <CollapsibleTrigger className="w-full" data-testid="section-songs-trigger">
                <SectionHeader icon={Search} title="Search & add songs" subtitle="Find individual tracks on YouTube Music" open={openSection === 'songs'} />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3 pl-1">
                <TrackSearch
                  onAddTrack={addOneCreatorTrack}
                  existingTrackIds={creatorIds}
                  placeholder="Search for songs..."
                  allowVideosToggle
                />
              </CollapsibleContent>
            </Collapsible>

            {/* Section 2: Community Playlists */}
            <Collapsible open={openSection === 'community'} onOpenChange={(v) => setOpenSection(v ? 'community' : null)}>
              <CollapsibleTrigger className="w-full" data-testid="section-community-trigger">
                <SectionHeader icon={ListMusic} title="Search community playlists" subtitle="Browse curated YT Music playlists" open={openSection === 'community'} />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3 pl-1">
                <CommunityPlaylistBrowser existingIds={creatorIds} onAddTracks={addCreatorTracks} />
              </CollapsibleContent>
            </Collapsible>

            {/* Section 3: Paste URL */}
            <Collapsible open={openSection === 'url'} onOpenChange={(v) => setOpenSection(v ? 'url' : null)}>
              <CollapsibleTrigger className="w-full" data-testid="section-url-trigger">
                <SectionHeader icon={Link2} title="Paste a YouTube URL" subtitle="Single video or a full playlist link" open={openSection === 'url'} />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3 pl-1">
                <div className="flex gap-2">
                  <Input
                    value={ytUrl}
                    onChange={(e) => setYtUrl(e.target.value)}
                    placeholder="Paste a YouTube playlist URL or ID"
                    className="bg-[#1A1A1A] border-white/10 flex-1"
                    data-testid="yt-url-input"
                  />
                  <Button
                    onClick={importYouTubeUrl}
                    disabled={!ytUrl.trim() || ytImporting}
                    className="bg-[#FF0000] hover:bg-[#CC0000] text-white"
                    data-testid="yt-url-import-button"
                  >
                    {ytImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Download className="w-4 h-4 mr-1" /> Import</>)}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Only music-category videos are kept. Max 200 tracks.</p>
                {importSkipped.length > 0 && (
                  <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3" data-testid="import-skipped-note">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-amber-300 text-xs font-semibold">
                        {importSkipped.length} song{importSkipped.length === 1 ? '' : 's'} skipped (unavailable / no playable version)
                      </p>
                      <button
                        onClick={() => setImportSkipped([])}
                        className="text-amber-400/70 hover:text-amber-300"
                        aria-label="Dismiss"
                        data-testid="import-skipped-dismiss"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <ul className="space-y-0.5 max-h-32 overflow-y-auto pr-1">
                      {importSkipped.map((s, i) => (
                        <li key={i} className="text-[11px] text-amber-100/80 truncate">
                          • {s.name}{s.artist ? ` — ${s.artist}` : ''}
                        </li>
                      ))}
                    </ul>
                    <p className="text-[10px] text-amber-400/60 mt-1.5">Search &amp; add replacements above if you want them in the playlist.</p>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Working list */}
            {creatorTracks.length > 0 ? (
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-400">{creatorTracks.length} songs · Drag to reorder</p>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setCreatorTracks([])}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid="creator-clear-all"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear all
                  </Button>
                </div>
                <ScrollArea className="h-64 pr-1">
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onCreatorDragEnd}>
                    <SortableContext items={creatorTracks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-1">
                        {creatorTracks.map((t, i) => (
                          <SortableTrackRow key={t.id} track={t} index={i} onRemove={removeCreatorTrack} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </ScrollArea>
              </div>
            ) : (
              <div className="border-t border-white/10 pt-6 text-center text-gray-500">
                <Music className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Songs you add will appear here</p>
              </div>
            )}
          </TabsContent>

          {/* ============= Tab 2: Generator ============= */}
          <TabsContent value="generator" className="flex-1 overflow-y-auto px-6 pt-4 pb-4 space-y-4 mt-0" data-testid="tab-content-generator">
            <div>
              <Label className="mb-2 block">Playlist name *</Label>
              <Input
                value={generatorName}
                onChange={(e) => setGeneratorName(e.target.value)}
                placeholder="e.g. Sunset Vibes"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="generator-name-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#FF007F]" />
                  Seed songs ({seedTracks.length}/5)
                </Label>
                <span className="text-xs text-gray-500">Pick 3-5 songs — we'll build 50 around them</span>
              </div>
              <TrackSearch
                onAddTrack={addSeed}
                existingTrackIds={seedTracks.map((t) => t.id)}
                placeholder="Search for a seed song..."
                allowVideosToggle
              />
              {seedTracks.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="seed-chips">
                  {seedTracks.map((t) => (
                    <div
                      key={t.id}
                      className="inline-flex items-center gap-2 pl-2 pr-1 py-1 rounded-full bg-[#FF007F]/15 border border-[#FF007F]/40"
                      data-testid={`seed-chip-${t.id}`}
                    >
                      {t.album_image && <img src={t.album_image} alt="" className="w-5 h-5 rounded-full object-cover" />}
                      <span className="text-white text-xs max-w-[180px] truncate">{t.name}</span>
                      <button onClick={() => removeSeed(t.id)} className="p-0.5 rounded-full hover:bg-white/10">
                        <X className="w-3 h-3 text-gray-300" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              onClick={generate}
              disabled={seedTracks.length < 3 || generating}
              className="w-full bg-[#FF007F] hover:bg-[#CC0066] text-black font-semibold h-11"
              data-testid="generate-button"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating 50 related tracks...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate 50-song playlist</>
              )}
            </Button>

            {generatedTracks.length > 0 && (
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-400">{generatedTracks.length} songs · Drag to reorder · Hover to swap or remove</p>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setGeneratedTracks([])}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    Clear
                  </Button>
                </div>
                <ScrollArea className="h-72 pr-1">
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onGeneratedDragEnd}>
                    <SortableContext items={generatedTracks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-1">
                        {generatedTracks.map((t, i) => (
                          <SortableTrackRow
                            key={t.id}
                            track={t}
                            index={i}
                            onRemove={removeGeneratedTrack}
                            onSwap={swapGeneratedTrack}
                            swapping={swappingId === t.id}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </ScrollArea>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer actions */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10 bg-black/30">
          <Button variant="outline" onClick={closeAndReset} className="border-white/10 text-gray-300 hover:text-white hover:bg-white/10">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#FF007F] hover:bg-[#CC0066] text-black font-semibold"
            data-testid="save-playlist-button"
          >
            {saving ? 'Saving...' : 'Save playlist'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlaylistCreatorDialog;
